import React, { useRef, useState, useEffect } from 'react';
import { fetchPayrollImports, type PayrollImportItem } from '../api/payroll';

interface Props {
  onSuccess?: (downloadUrl: string) => void;
  showHeader?: boolean;
}

const PayrollUploader: React.FC<Props> = ({ onSuccess, showHeader = true }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<PayrollImportItem[]>([]);
  const [showToast, setShowToast] = useState<null | { type: 'success' | 'error'; message: string }>(null);
  const [progress, setProgress] = useState<string | null>(null);

  const loadHistory = async () => {
    try {
      const data = await fetchPayrollImports();
      setHistory(data);
    } catch (e) {
      // pas bloquant
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const handleChoose = () => inputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!(name.endsWith('.xlsx') || name.endsWith('.xlsm'))) {
      setError('Format non supporté. Utilisez un fichier .xlsx ou .xlsm');
      setFile(null);
      return;
    }
    setError(null);
    setFile(f);
  };

  const handleDownload = async (jobId: number, filename: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://192.168.1.4:8000/api/';
      const token = localStorage.getItem('access');

      const res = await fetch(`${apiUrl}payroll/download/${jobId}/`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Fichier introuvable. Il a peut-être été généré avant la mise à jour du système');
        }
        throw new Error(`Erreur ${res.status}: Impossible de télécharger le fichier`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      setShowToast({ type: 'success', message: 'Téléchargement démarré!' });
      setTimeout(() => setShowToast(null), 2000);
    } catch (err: any) {
      setShowToast({ type: 'error', message: err.message || 'Erreur lors du téléchargement' });
      setTimeout(() => setShowToast(null), 4000);
    }
  };

  const handleDelete = async (jobId: number) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce fichier ZIP ?')) {
      return;
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://192.168.1.4:8000/api/';
      const token = localStorage.getItem('access');

      const res = await fetch(`${apiUrl}payroll/delete/${jobId}/`, {
        method: 'DELETE',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) {
        throw new Error(`Erreur ${res.status}: Impossible de supprimer le fichier`);
      }

      // Supprimer immédiatement de l'état local pour feedback instantané
      setHistory(prev => prev.filter(item => item.id !== jobId));

      // Recharger l'historique depuis le serveur pour confirmation
      await loadHistory();

      setShowToast({ type: 'success', message: 'Fichier supprimé avec succès!' });
      setTimeout(() => setShowToast(null), 2000);
    } catch (err: any) {
      setShowToast({ type: 'error', message: err.message || 'Erreur lors de la suppression' });
      setTimeout(() => setShowToast(null), 4000);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();

  const handleUpload = async () => {
    if (!file) return;
    setError(null);
    setUploading(true);
    setProgress('Envoi du fichier...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('async', 'true'); // Mode asynchrone pour gros fichiers (>50 lignes)
      
      const apiUrl = import.meta.env.VITE_API_URL || 'http://192.168.1.4:8000/api/';
      const token = localStorage.getItem('access');
      
      const res = await fetch(`${apiUrl}payroll/bulk-generate/`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: formData,
      });
      
      if (!res.ok) {
        const msg = res.status === 400 ? 'Format non supporté (.xlsx ou .xlsm requis)' : `Erreur serveur (${res.status})`;
        throw new Error(msg);
      }
      
      // IMPORTANT: Lire le Content-Type avant de consommer le corps
      const contentType = res.headers.get('content-type');
      const isJson = contentType?.includes('application/json');
      
      // Si JSON, c'est soit un test, soit une réponse async (202)
      if (isJson) {
        const data = await res.json();
        
        // Mode TEST: afficher la réponse
        if (data.status === 'test_ok') {
          setShowToast({ 
            type: 'success', 
            message: `Test OK: ${data.message} (${data.filename}, ${data.size} bytes)` 
          });
          setTimeout(() => setShowToast(null), 5000);
          setUploading(false);
          setProgress(null);
          return;
        }
        
        // Vérifier si c'est une réponse asynchrone (202)
        if (res.status === 202 && data.job_id) {
        // Mode asynchrone : poller le statut
        setProgress(`Génération de ${data.total_rows} bulletins en cours...`);
        
        // Poller le statut toutes les 3 secondes
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await fetch(`${apiUrl}payroll/job-status/${data.job_id}/`, {
              headers: {
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
              },
            });
            
            if (statusRes.ok) {
              const status = await statusRes.json();
              
              if (status.status === 'completed') {
                clearInterval(pollInterval);
                setProgress('Téléchargement...');
                
                // Télécharger le ZIP avec authentification
                try {
                  const downloadRes = await fetch(status.download_url, {
                    headers: {
                      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                    },
                  });
                  
                  if (!downloadRes.ok) {
                    throw new Error(`Erreur téléchargement: ${downloadRes.status}`);
                  }
                  
                  const blob = await downloadRes.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `bulletins_job_${data.job_id}.zip`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  
                  onSuccess?.(url);
                  setFile(null);
                  setProgress(null);
                  await loadHistory();
                  setShowToast({ 
                    type: 'success', 
                    message: `${status.bulletin_count} bulletins générés avec succès! Le téléchargement a démarré.` 
                  });
                  setTimeout(() => setShowToast(null), 3500);
                  setUploading(false);
                } catch (downloadError: any) {
                  console.error('Erreur téléchargement:', downloadError);
                  setError(downloadError.message || 'Erreur lors du téléchargement');
                  setShowToast({ type: 'error', message: 'Erreur lors du téléchargement du ZIP.' });
                  setTimeout(() => setShowToast(null), 4000);
                  setUploading(false);
                  setProgress(null);
                }
                
              } else {
                // Toujours en cours
                setProgress(status.message || 'Génération en cours...');
              }
            }
          } catch (pollError) {
            console.error('Erreur polling:', pollError);
          }
        }, 3000); // Poll toutes les 3 secondes
        
        // Timeout de sécurité : 10 minutes max
        setTimeout(() => {
          clearInterval(pollInterval);
          if (uploading) {
            setError('Timeout : la génération prend trop de temps');
            setUploading(false);
            setProgress(null);
          }
        }, 600000); // 10 minutes
        
      } else {
        // Mode synchrone : pas JSON, donc c'est un blob (ZIP)
        const generatedCount = res.headers.get('X-Generated-Count');
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bulletins.zip';
        a.click();
        onSuccess?.(url);
        setFile(null);
        await loadHistory();
        setShowToast({ type: 'success', message: `Bulletins générés avec succès (${generatedCount || '?'}). Le téléchargement a démarré.` });
        setTimeout(() => setShowToast(null), 3500);
        setUploading(false);
      }
      
      } else {
        // Réponse non-JSON : c'est un blob (ZIP en mode synchrone)
        const generatedCount = res.headers.get('X-Generated-Count');
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bulletins.zip';
        a.click();
        onSuccess?.(url);
        setFile(null);
        await loadHistory();
        setShowToast({ type: 'success', message: `Bulletins générés avec succès (${generatedCount || '?'}). Le téléchargement a démarré.` });
        setTimeout(() => setShowToast(null), 3500);
        setUploading(false);
      }
      
    } catch (e: any) {
      setError(e?.message || 'Erreur lors de la génération');
      setShowToast({ type: 'error', message: e?.message || 'Erreur lors de la génération des bulletins.' });
      setTimeout(() => setShowToast(null), 4000);
      setUploading(false);
      setProgress(null);
    }
  };

  return (
    <div className="card bg-base-100 shadow-md overflow-hidden border border-base-300 relative">
      {showHeader && (
        <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 px-6 py-4 border-b border-base-300">
          <h3 className="text-xl font-bold text-primary">Bulletins de paie</h3>
          <p className="text-sm text-base-content/70">Importez un fichier Excel (.xlsx) et générez automatiquement un ZIP de bulletins</p>
        </div>
      )}
      <div className="card-body gap-6">
        {/* Indicateur de progression */}
        {uploading && progress && (
          <div className="alert alert-info">
            <div className="flex items-center gap-3">
              <span className="loading loading-spinner loading-sm"></span>
              <div>
                <div className="font-bold">Génération en cours</div>
                <div className="text-sm">{progress}</div>
              </div>
            </div>
          </div>
        )}
        
        {/* Zone d'upload */}
        <div
          className={`w-full rounded-xl border-2 border-dashed ${file ? 'border-primary bg-primary/5' : 'border-base-300'} p-6 flex flex-col items-center justify-center text-center transition-colors`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-warning mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="text-sm mb-2">Glissez-déposez votre fichier Excel ici</p>
          <p className="text-xs text-base-content/60 mb-4">Formats acceptés: .xlsx, .xlsm</p>
          <div className="flex gap-3">
            <button className="btn btn-warning btn-sm" onClick={handleChoose} disabled={uploading}>
              Choisir un fichier
            </button>
            {file && (
              <button className={`btn btn-success btn-sm ${uploading ? 'loading' : ''}`} onClick={handleUpload} disabled={uploading}>
                Générer les bulletins
              </button>
            )}
          </div>
          <input ref={inputRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={handleFileChange} />
          {file && (
            <div className="mt-3 text-xs text-base-content/70">
              Fichier sélectionné: <span className="font-semibold">{file.name}</span>
            </div>
          )}
          {error && (
            <div className="alert alert-error mt-4">
              <span>{error}</span>
            </div>
          )}
        </div>

  {/* Historique */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold">Historique récent</h4>
            <button className="btn btn-ghost btn-xs" onClick={loadHistory}>Rafraîchir</button>
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-base-content/60">Aucun import pour le moment.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Documents</th>
                    <th>Lignes</th>
                    <th>Générés</th>
                    <th>ZIP</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.slice(0, 8).map(item => {
                    const createdAt = new Date(item.created_at);

                    return (
                    <tr key={item.id}>
                      <td className="text-xs">{createdAt.toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</td>
                      <td className="text-sm">
                        Bulletins de paie
                      </td>
                      <td className="text-center">{item.total_rows}</td>
                      <td className="text-center font-semibold text-success">{item.bulletin_count}</td>
                      <td>
                        {item.bulletin_count > 0 ? (
                          <button
                            className="btn btn-sm btn-warning gap-2"
                            onClick={() => handleDownload(item.id, `bulletins_${item.id}.zip`)}
                            title="Télécharger le ZIP"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            ZIP
                          </button>
                        ) : (
                          <span className="text-xs text-base-content/50">Aucun bulletin</span>
                        )}
                      </td>
                      <td>
                        {item.bulletin_count > 0 && (
                          <button
                            className="btn btn-sm btn-ghost btn-circle text-error"
                            onClick={() => handleDelete(item.id)}
                            title="Supprimer le fichier ZIP"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {showToast && (
        <div className="toast toast-end z-50">
          <div className={`alert ${showToast.type === 'success' ? 'alert-success' : 'alert-error'} shadow-lg`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M12 20.5a8.5 8.5 0 100-17 8.5 8.5 0 000 17z" />
            </svg>
            <span>{showToast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollUploader;
